import React, { useMemo, useState } from "react";
import { supabase } from "@/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import Button from "@/ui/Button.jsx";
import WidgetShell from "@/ui/WidgetShell.jsx";
import { SmallText } from "@/ui/Typography.jsx";

const MIN_PASS = 8;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AccountSecurityPanel() {
  const { email, signInWithPassword, updatePassword, getCurrentUser } = useAuth() || {};

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passError, setPassError] = useState("");
  const [passSuccess, setPassSuccess] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  const canReauth = useMemo(() => !!email, [email]);
  if (!canReauth) return null;

  const reauthenticate = async (pwd) => {
    // Re-auth with current email + password
    await signInWithPassword?.(email, pwd);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPassError("");
    setPassSuccess("");
    if (!currentPassword.trim()) {
      setPassError("Enter your current password.");
      return;
    }
    if (!newPassword || newPassword.length < MIN_PASS) {
      setPassError(`New password must be at least ${MIN_PASS} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPassError("New passwords do not match.");
      return;
    }

    setPassLoading(true);
    try {
      await reauthenticate(currentPassword);
      await updatePassword?.(newPassword);
      setPassSuccess("Password updated. Other sessions may be signed out.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPassError("Incorrect current password or unable to update password.");
    } finally {
      setPassLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError("");
    setEmailSuccess("");

    if (!emailPassword.trim()) {
      setEmailError("Enter your current password.");
      return;
    }
    if (!newEmail.trim() || !emailRegex.test(newEmail.trim())) {
      setEmailError("Enter a valid new email address.");
      return;
    }

    setEmailLoading(true);
    try {
      await reauthenticate(emailPassword);
      const { error } = await supabase.auth.updateUser({
        email: newEmail.trim(),
      });
      if (error) throw error;
      await getCurrentUser?.();
      setEmailSuccess(
        "We’ve sent a confirmation to your new email. It will update after you confirm."
      );
      setNewEmail("");
      setEmailPassword("");
    } catch (err) {
      setEmailError(err?.message || "Could not change email. Check your password and try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 bg-white/70 dark:bg-white/5">
        <div className="mb-3">
          <div className="text-base font-semibold">Change password</div>
          <div className="text-sm opacity-70">Requires your current password.</div>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-3">
          <div className="flex flex-col gap-2">
            <input
              type="password"
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 bg-white/90 dark:bg-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            />
            <input
              type="password"
              placeholder={`New password (min ${MIN_PASS} chars)`}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 bg-white/90 dark:bg-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 bg-white/90 dark:bg-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            />
          </div>
          {passError && <div className="text-sm text-red-600">{passError}</div>}
          {passSuccess && <div className="text-sm text-emerald-600">{passSuccess}</div>}
          <button
            type="submit"
            disabled={passLoading}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {passLoading ? "Updating..." : "Update password"}
          </button>
        </form>
      </div>

      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 bg-white/70 dark:bg-white/5">
        <div className="mb-3">
          <div className="text-base font-semibold">Change login email</div>
          <div className="text-sm opacity-70">
            Requires your current password. A confirmation email will be sent to the new address.
          </div>
        </div>
        <form onSubmit={handleChangeEmail} className="space-y-3">
          <div className="flex flex-col gap-2">
            <input
              type="password"
              placeholder="Current password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 bg-white/90 dark:bg-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            />
            <input
              type="email"
              placeholder="New email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 bg-white/90 dark:bg-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            />
          </div>
          {emailError && <div className="text-sm text-red-600">{emailError}</div>}
          {emailSuccess && <div className="text-sm text-emerald-600">{emailSuccess}</div>}
          <button
            type="submit"
            disabled={emailLoading}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {emailLoading ? "Sending..." : "Update email"}
          </button>
        </form>
      </div>
    </div>
  );
}
